import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

import { getStationLogo } from "../utils/index";
import * as config from "../config";


const useStationHistory = (stationId, fetchHistory) => {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    if (fetchHistory) {
      (async () => {
        const response = await fetch(`${config.SERVER_URL}/stations/${stationId}/history`);
        const data = await response.json();
        setHistory(data);
      })();
    }
  }, [stationId, fetchHistory]);

  return { history }
}

const FuelTypePrice = ({ fuelName, fuelType, price, backgroundColor }) => {
  return <div className="h-20 border border-gray-400 flex flex-col">
    <div className={`flex-1 flex flex-col items-center justify-center ${backgroundColor} ${backgroundColor === "bg-black" && "text-white"} font-bold w-full text-center`}>
      <span className="text-sm">{fuelName}</span>
      <span className="text-sm">{fuelType}</span>
    </div>
    <span className="font-bold border-t w-full text-center">{price} €</span>
  </div>
}

export const StationCard = ({ station, showHistoryChart = false, isMobile = false }) => {
  const { history } = useStationHistory(station.station_id, showHistoryChart);

  const minDiesel = history ? Math.min(...history?.diesel_a_history.map(x => x.price).filter(Boolean)) : 0;
  const maxDiesel = history ? Math.max(...history?.diesel_a_history.map(x => x.price).filter(Boolean)) : 0;
  const minGasoline = history ? Math.min(...history?.gasoline_95_e5_history.map(x => x.price).filter(Boolean)) : 0;
  const maxGasoline = history ? Math.max(...history?.gasoline_95_e5_history.map(x => x.price).filter(Boolean)) : 0;

  const minChart = minDiesel < minGasoline ? minDiesel : minGasoline;
  const maxChart = maxDiesel > maxGasoline ? maxDiesel : maxGasoline;

  return <div className={`${!showHistoryChart && "shadow-lg"} rounded-xl p-5 text-gray-800 flex flex-col`}>
    <div className="flex items-center">
      <img
        src={getStationLogo(station.name)}
        alt="Logo gasolinera"
        className="w-12 h-12 mr-2"
      />
      <span className="font-bold text-base md:text-xl text-ellipsis">{station.name}</span>
    </div>
    <span className="block my-2 text-sm md:text-base">{station.address}, {station.postal_code}, {station.locality}, {station.province}</span>
    <small className="block my-1">
      Venta {station.type_of_sell === "P" ? "al público" : "a cooperativistas"} ({station.type_of_sell})
    </small>
    <small className="block mb-2">Actualizado el {new Date(station.bulk_insert_id).toLocaleString()}</small>
    <div
      style={{
        gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))"
      }}
      className="grid gap-2"
    >
      {station.price_gasoline_95_e5 && <FuelTypePrice
        fuelName="Gasolina"
        fuelType="95 E5"
        price={station.price_gasoline_95_e5}
        backgroundColor="bg-red-500"
      />}
      {station.price_gasoline_95_e10 && <FuelTypePrice
        fuelName="Gasolina"
        fuelType="95 E10"
        price={station.price_gasoline_95_e10}
        backgroundColor="bg-orange-500"
      />}
      {station.price_gasoline_95_e5_premium && <FuelTypePrice
        fuelName="Gasolina"
        fuelType="95 Prem."
        price={station.price_gasoline_95_e5_premium}
        backgroundColor="bg-yellow-500"
      />}
      {station.price_gasoline_98_e5 && <FuelTypePrice
        fuelName="Gasolina"
        fuelType="98 E5"
        price={station.price_gasoline_98_e5}
        backgroundColor="bg-lime-500"
      />}
      {station.price_gasoline_98_e10 && <FuelTypePrice
        fuelName="Gasolina"
        fuelType="98 E10"
        price={station.price_gasoline_98_e10}
        backgroundColor="bg-green-500"
      />}
      {station.price_diesel_a && <FuelTypePrice
        fuelName="Diésel"
        fuelType="A"
        price={station.price_diesel_a}
        backgroundColor="bg-black"
      />}
      {station.price_diesel_premium && <FuelTypePrice
        fuelName="Diésel"
        fuelType="Premium"
        price={station.price_diesel_premium}
        backgroundColor="bg-sky-500"
      />}
      {station.price_diesel_b && <FuelTypePrice
        fuelName="Diésel"
        fuelType="B"
        price={station.price_diesel_b}
        backgroundColor="bg-violet-500"
      />}
    </div >
    {showHistoryChart && <div className={`w-full ${isMobile ? "h-[150px]" : "h-[250px]"} flex items-center justify-center mt-2`}>
      {!history ?
        <div className="w-16 h-16 border-[8px] border-t-primary rounded-full animate-spin" />
        :
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={new Array(7).fill(undefined).map((_, i) => {
              const date = new Date(history.diesel_a_history[i].date);

              return {
                date: `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}`,
                "Gasolina 95 E5": history.gasoline_95_e5_history[i].price,
                "Diésel A": history.diesel_a_history[i].price,
              }
            })}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" interval={0} />
            <YAxis
              yAxisId="leftYAxis"
              type="number"
              domain={[minChart - 0.2 > 0 ? minChart - 0.2 : 0, maxChart + 0.2]}
            />
            <Tooltip />
            <Line yAxisId="leftYAxis" type="monotone" dataKey="Gasolina 95 E5" stroke="#EF4444" strokeWidth={3} />
            <Line yAxisId="leftYAxis" type="monotone" dataKey="Diésel A" stroke="#000000" strokeWidth={3} />
            <Legend />
          </LineChart>
        </ResponsiveContainer>
      }
    </div>}
  </div>
}